// src/components/landing/ContactCTA.tsx
// Contact form for employers/customers — submits to /api/contact, which
// stores the lead in Postgres and sends a Brevo notification + auto-reply.
import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle, Sparkles, HeartHandshake, Loader2 } from 'lucide-react';
import { InfoBox } from '@/components/custom-ui/info-box';
import CONFIG from '@/lib/config';
import { contactApi } from '@/lib/api/contactApi';

interface ContactCTAProps {
    mode?: 'standalone' | 'embedded';
}

const CONTACT_EMAIL = CONFIG.CONTACT_EMAIL;

export const ContactCTA: React.FC<ContactCTAProps> = ({ mode = 'standalone' }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        organisation: '',
        message: '',
        website: '', // honeypot — must stay empty
    });
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        setError(null);

        if (!formData.name.trim()) {
            setError('Namn är obligatoriskt');
            return;
        }

        if (!formData.email.trim()) {
            setError('E-post är obligatoriskt');
            return;
        }

        if (!validateEmail(formData.email)) {
            setError('Ogiltig e-postadress');
            return;
        }

        if (!formData.organisation.trim()) {
            setError('Organisation är obligatoriskt');
            return;
        }

        try {
            setIsSubmitting(true);
            await contactApi.submitContactForm({
                name: formData.name.trim(),
                email: formData.email.trim(),
                municipality: formData.organisation.trim(),
                message: formData.message.trim(),
                type: 'demo',
                website: formData.website,
            });
            setSubmitted(true);
        } catch (err) {
            console.error('[ContactCTA] submit failed:', err);
            setError(`Kunde inte skicka förfrågan just nu. Försök igen, eller maila oss direkt på ${CONTACT_EMAIL}.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setSubmitted(false);
        setError(null);
        setFormData({ name: '', email: '', organisation: '', message: '', website: '' });
    };

    // Reusable form content
    const formFields = (
        <div className="space-y-4">
            {/* Form Fields */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-semibold mb-1.5 text-[#212121]">Namn *</label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full px-3 py-2.5 text-sm bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                        placeholder="Ditt namn"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold mb-1.5 text-[#212121]">E-post *</label>
                    <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full px-3 py-2.5 text-sm bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                        placeholder="din@email.se"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-semibold mb-1.5">Organisation *</label>
                <input
                    type="text"
                    value={formData.organisation}
                    onChange={(e) => setFormData({ ...formData, organisation: e.target.value })}
                    required
                    className="w-full px-3 py-2.5 text-sm bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent transition-all"
                    placeholder="Ditt företag eller organisation"
                />
            </div>

            <div>
                <label className="block text-xs font-semibold mb-1.5">Meddelande (valfritt)</label>
                <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2.5 text-sm bg-white border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1A4D4F] focus:border-transparent resize-none transition-all"
                    placeholder="Berätta gärna mer om hur vi kan hjälpa er..."
                />
            </div>

            <p className="text-xs text-gray-500 text-center">
                <span className="text-red-500">*</span> Obligatoriska fält
            </p>

            {/* Honeypot — invisible to humans, attractive to bots. Must stay empty. */}
            <input
                type="text"
                name="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0, pointerEvents: 'none' }}
            />
        </div>
    );

    const submitButton = (
        <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm disabled:opacity-60 disabled:cursor-not-allowed"
        >
            {isSubmitting ? (
                <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Skickar...
                </>
            ) : (
                <>
                    <Send className="h-4 w-4" />
                    Skicka meddelande
                </>
            )}
        </button>
    );

    const successContent = (
        <div className="flex flex-col items-center justify-center py-8 px-4">
            <div className="relative mb-6">
                <div className="w-20 h-20 mx-auto bg-[#1A4D4F] rounded-full flex items-center justify-center shadow-lg shadow-[#1A4D4F]/30 animate-bounce">
                    <CheckCircle className="w-10 h-10 text-[#f5f5f5]" />
                </div>
                <div className="absolute -top-2 -right-2 animate-pulse">
                    <Sparkles className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                </div>
            </div>
            <h3 className="text-2xl font-bold text-[#1A4D4F] mb-2 text-center">Tack för din förfrågan!</h3>
            <p className="text-sm text-[#212121] text-center mb-2 max-w-sm leading-relaxed">
                Vi har tagit emot ditt meddelande och återkommer så snart vi kan.
            </p>
            <p className="text-xs text-[#212121]/70 text-center mb-6 max-w-sm">
                Ett bekräftelsemejl är på väg till {formData.email || 'din inkorg'}. Du kan även nå oss på{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#1A4D4F] font-semibold underline underline-offset-2 hover:no-underline">{CONTACT_EMAIL}</a>.
            </p>
            <button
                onClick={resetForm}
                className="px-6 py-2.5 bg-[#1A4D4F] text-[#f5f5f5] font-semibold rounded-xl hover:bg-[#1A4D4F]/90 transition-all text-sm"
            >
                Skicka nytt meddelande
            </button>
        </div>
    );

    // Embedded mode - lean layout for slide-over panels
    if (mode === 'embedded') {
        if (submitted) {
            return (
                <div className="flex flex-col h-full">
                    <div className="flex-1 flex items-center justify-center px-6">
                        {successContent}
                    </div>
                </div>
            );
        }

        return (
            <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {error && (
                        <InfoBox variant="error" className="mb-4">
                            {error}
                        </InfoBox>
                    )}
                    {formFields}
                </div>
                <div className="flex-shrink-0 border-t-2 border-gray-100 bg-gray-50 px-6 py-4">
                    {submitButton}
                </div>
            </div>
        );
    }

    // Standalone mode - full card with header
    if (submitted) {
        return (
            <div className="w-full max-w-2xl mx-auto h-full flex items-center justify-center p-6 sm:p-8">
                <div className="w-full max-h-[85vh] bg-[#A4C3B2] rounded-2xl flex flex-col overflow-hidden">
                    <div className="flex-1 flex items-center justify-center p-6">
                        {successContent}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-2xl mx-auto h-full flex items-center justify-center p-6 sm:p-8">
            <div className="w-full max-h-[85vh] bg-[#A4C3B2] rounded-2xl flex flex-col overflow-hidden">
                <div className="flex-shrink-0 p-5">
                    <div className="text-center">
                        <div className="mx-auto mb-3">
                            <HeartHandshake className="w-14 h-14 text-[#212121] stroke-[1.2] mx-auto" />
                        </div>
                        <h2 className="text-lg font-bold text-[#212121] mb-1">
                            Intresserad av att samarbeta med Nexilink?
                        </h2>
                        <p className="text-sm text-[#212121]/70">
                            Kontakta oss så berättar vi mer om hur vi kan hjälpa er organisation
                        </p>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pb-4">
                    {error && (
                        <InfoBox variant="error" className="mb-4">
                            {error}
                        </InfoBox>
                    )}
                    {formFields}
                </div>

                <div className="flex-shrink-0 border-t border-[#1A4D4F]/10 p-4">
                    {submitButton}
                </div>
            </div>
        </div>
    );
};
